import { getProgressionDrillById } from '../data/progression/drills';
import { getLessonFlowForSkill } from '../data/progression/lessonFlows';
import { getProgressionSkillById, getProgressionSkillsForSport } from '../data/progression/skills';
import { getSkillTracksForSport } from '../data/progression/skillTracks';
import { LESSON_STAGE_KEYS } from './progressionConstants';

export const LESSON_STAGE_COMPLETION_OPTIONS = [
  'not_attempted',
  'attempted',
  'completed',
];

export const LESSON_OUTCOME_STATUS_OPTIONS = [
  'in_progress',
  'completed',
  'needs_review',
];

export const LESSON_SKILL_CONFIDENCE_OPTIONS = [
  'low',
  'developing',
  'solid',
  'ready_to_progress',
];

function formatStageTitle(stageKey) {
  return String(stageKey || '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function sanitizeText(value) {
  return String(value || '').trim();
}

function buildStagePlan(stageKey, lessonFlow, existingStage = null) {
  const plannedDrillIds = lessonFlow?.stages?.[stageKey] || [];
  const plannedDrills = plannedDrillIds
    .map((drillId) => getProgressionDrillById(drillId))
    .filter(Boolean);

  return {
    stage: stageKey,
    title: formatStageTitle(stageKey),
    plannedDrillIds,
    plannedDrills,
    instructorNotes: sanitizeText(existingStage?.instructorNotes),
    completionStatus: LESSON_STAGE_COMPLETION_OPTIONS.includes(existingStage?.completionStatus)
      ? existingStage.completionStatus
      : 'not_attempted',
  };
}

function resolveTargetSkillId({ sport, targetSkillId, summary }) {
  if (targetSkillId && getProgressionSkillById(targetSkillId)?.sport === sport) {
    return targetSkillId;
  }

  const recommendedCandidates = [
    summary?.nextSkill?.skillId || '',
    summary?.currentSkill?.skillId || '',
  ].filter(Boolean);

  const recommendedWithLessonFlow = recommendedCandidates.find((skillId) => {
    const skill = getProgressionSkillById(skillId);
    return skill?.sport === sport && getLessonFlowForSkill(skillId);
  });
  if (recommendedWithLessonFlow) {
    return recommendedWithLessonFlow;
  }

  const recommendedExistingSkill = recommendedCandidates.find((skillId) => {
    const skill = getProgressionSkillById(skillId);
    return skill?.sport === sport;
  });
  if (recommendedExistingSkill) {
    return recommendedExistingSkill;
  }

  const sportSkills = getProgressionSkillsForSport(sport);
  const coreSkill = sportSkills.find((skill) => skill.trackId === `${sport === 'skiing' ? 'ski' : 'snowboard'}_progression_core`);
  return coreSkill?.skillId || sportSkills[0]?.skillId || '';
}

export function buildInstructorLessonPlan({
  videoId = '',
  studentId = '',
  instructorId = '',
  studentName = '',
  studentEmail = '',
  sport = '',
  targetSkillId = '',
  summary = null,
  existingLesson = null,
} = {}) {
  if (!sport) {
    return {
      lessonId: '',
      videoId,
      studentId,
      instructorId,
      studentName,
      studentEmail,
      sport: '',
      trackId: '',
      trackName: 'Core Progression',
      targetSkillId: '',
      targetSkillName: '',
      lessonFlowId: '',
      currentLevel: summary?.currentLevel || '',
      recommendedBySystem: false,
      recommendationReason: '',
      stages: [],
      hasLessonFlow: false,
      lessonFlowEmptyText: 'Choose a skill to build a lesson plan.',
      lessonOutcome: {
        status: 'in_progress',
        skillConfidence: 'developing',
        recommendedNextSkillId: '',
        instructorSummary: '',
      },
    };
  }

  const resolvedTargetSkillId = resolveTargetSkillId({ sport, targetSkillId, summary });
  const skill = getProgressionSkillById(resolvedTargetSkillId);
  const track =
    getSkillTracksForSport(sport).find((item) => item.trackId === skill?.trackId) ||
    getSkillTracksForSport(sport)[0] ||
    null;
  const lessonFlow = skill ? getLessonFlowForSkill(skill.skillId) : null;
  const recommendedSkillId = summary?.nextSkill?.skillId || summary?.currentSkill?.skillId || '';
  const stages = LESSON_STAGE_KEYS.map((stageKey) =>
    buildStagePlan(
      stageKey,
      lessonFlow,
      existingLesson?.stages?.find((item) => item.stage === stageKey) || null
    )
  );
  const recommendedNextSkillId =
    existingLesson?.lessonOutcome?.recommendedNextSkillId ||
    skill?.nextSkillIds?.[0] ||
    summary?.nextSkill?.skillId ||
    '';

  return {
    lessonId:
      existingLesson?.lessonId ||
      `lesson_draft_${studentId || 'student'}_${sport || 'sport'}_${resolvedTargetSkillId || 'manual'}`,
    videoId: existingLesson?.videoId || videoId || '',
    studentId,
    instructorId,
    studentName,
    studentEmail,
    sport,
    trackId: skill?.trackId || track?.trackId || '',
    trackName: track?.name || 'Core Progression',
    targetSkillId: skill?.skillId || '',
    targetSkillName: skill?.name || 'Choose a skill',
    targetSkillDescription: skill?.description || '',
    lessonFlowId: lessonFlow?.lessonFlowId || '',
    lessonFlowTitle: skill ? `Lesson flow for ${skill.name}` : 'Lesson flow',
    currentLevel: summary?.currentLevel || skill?.level || '',
    recommendedBySystem: Boolean(recommendedSkillId) && recommendedSkillId === skill?.skillId,
    recommendationReason: sanitizeText(summary?.recommendationReason || existingLesson?.recommendationReason),
    currentSkillName: summary?.currentSkill?.name || '',
    nextSkillName: summary?.nextSkill?.name || '',
    stages,
    hasLessonFlow: Boolean(lessonFlow),
    lessonFlowEmptyText: skill
      ? 'A staged lesson flow has not been added for this skill yet. You can still plan the lesson manually.'
      : 'Choose a skill to load the staged lesson flow.',
    lessonOutcome: {
      status: LESSON_OUTCOME_STATUS_OPTIONS.includes(existingLesson?.lessonOutcome?.status)
        ? existingLesson.lessonOutcome.status
        : 'in_progress',
      skillConfidence: LESSON_SKILL_CONFIDENCE_OPTIONS.includes(existingLesson?.lessonOutcome?.skillConfidence)
        ? existingLesson.lessonOutcome.skillConfidence
        : 'developing',
      recommendedNextSkillId: recommendedNextSkillId || '',
      instructorSummary: sanitizeText(existingLesson?.lessonOutcome?.instructorSummary),
    },
  };
}
